import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Radio, Checkbox, Input, Button, Spin, Alert } from 'antd';
import { PrimeArtifact } from '../../types/primeArtifact';
import { EnhancedArtifact } from '../../types/artifacts';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { PrimeToCompanyImportService, PrimeToCompanyImportRequest } from '../../services/primeToCompanyImportService';
import styles from './NodePrimeImportDialog.module.css';

interface NodePrimeImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  companyArtifact: EnhancedArtifact;
  onImportComplete: () => void;
  companyId?: string;
  companyArtifacts: EnhancedArtifact[]; // All company artifacts to check for duplicates
}

interface PrimeArtifactSelection {
  primeArtifact: PrimeArtifact;
  companyName: string;
  selected: boolean;
}

type ImportType = 'children' | 'peers';

export const NodePrimeImportDialog: React.FC<NodePrimeImportDialogProps> = ({
  isOpen,
  onClose,
  companyArtifact,
  onImportComplete,
  companyId,
  companyArtifacts,
}) => {
  const [importType, setImportType] = useState<ImportType>('children');
  const [primeArtifacts, setPrimeArtifacts] = useState<PrimeArtifact[]>([]);
  const [selections, setSelections] = useState<PrimeArtifactSelection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if artifact has prime_artifact_id
  const hasPrimeArtifact = !!companyArtifact.prime_artifact_id;

  // Load prime artifacts (children or peers) when dialog opens or import type changes
  useEffect(() => {
    if (isOpen && hasPrimeArtifact && companyArtifact.prime_artifact_id) {
      void loadPrimeArtifacts();
    } else if (isOpen && !hasPrimeArtifact) {
      setError('This artifact does not have a linked prime artifact. Cannot import.');
      setPrimeArtifacts([]);
      setSelections([]);
    }
  }, [isOpen, importType, companyArtifact.prime_artifact_id, hasPrimeArtifact]);

  const loadPrimeArtifacts = async () => {
    if (!companyArtifact.prime_artifact_id) return;

    setIsLoading(true);
    setError(null);
    try {
      let artifacts: PrimeArtifact[];
      
      if (importType === 'children') {
        artifacts = await PrimeArtifactService.getPrimeArtifactChildren(companyArtifact.prime_artifact_id);
      } else {
        artifacts = await PrimeArtifactService.getPrimeArtifactPeers(companyArtifact.prime_artifact_id);
      }

      // Flatten the hierarchy to get all company artifacts for duplicate checking
      const flattenArtifacts = (artifacts: EnhancedArtifact[]): EnhancedArtifact[] => {
        const result: EnhancedArtifact[] = [];
        const stack = [...artifacts];
        while (stack.length) {
          const artifact = stack.pop();
          if (!artifact) continue;
          result.push(artifact);
          if (artifact.children && artifact.children.length > 0) {
            stack.push(...artifact.children);
          }
        }
        return result;
      };

      const allCompanyArtifacts = flattenArtifacts(companyArtifacts);
      
      // Filter out artifacts that are already imported
      const existingPrimeIds = new Set(
        allCompanyArtifacts
          .filter((a) => a.prime_artifact_id)
          .map((a) => a.prime_artifact_id!)
      );

      const availableArtifacts = artifacts.filter(
        (artifact) => !existingPrimeIds.has(artifact.id)
      );

      setPrimeArtifacts(availableArtifacts);

      // Initialize selections
      const initialSelections: PrimeArtifactSelection[] = availableArtifacts.map((artifact) => ({
        primeArtifact: artifact,
        companyName: artifact.name,
        selected: false,
      }));
      setSelections(initialSelections);

      if (availableArtifacts.length === 0) {
        setError(
          importType === 'children'
            ? 'No children available to import, or all children are already imported.'
            : 'No peers available to import, or all peers are already imported.'
        );
      }
    } catch (err: any) {
      console.error('[NodePrimeImportDialog] Error loading prime artifacts:', err);
      setError(
        err.response?.data?.detail ||
        err.message ||
        `Failed to load ${importType === 'children' ? 'children' : 'peers'} from prime artifact`
      );
      setPrimeArtifacts([]);
      setSelections([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectionChange = (primeId: string, selected: boolean) => {
    setSelections((prev) =>
      prev.map((sel) =>
        sel.primeArtifact.id === primeId ? { ...sel, selected } : sel
      )
    );
  };

  const handleNameChange = (primeId: string, name: string) => {
    setSelections((prev) =>
      prev.map((sel) =>
        sel.primeArtifact.id === primeId ? { ...sel, companyName: name } : sel
      )
    );
  };

  const handleImport = async () => {
    const selectedSelections = selections.filter((sel) => sel.selected);
    
    if (selectedSelections.length === 0) {
      setError('Please select at least one artifact to import.');
      return;
    }

    setIsImporting(true);
    setError(null);

    try {
      // Determine parent_id based on import type
      let parentId: string | undefined;
      if (importType === 'children') {
        // For children, parent is the selected company artifact
        parentId = companyArtifact.id;
      } else {
        // For peers, parent is the selected artifact's parent (so they're siblings)
        parentId = companyArtifact.parent_ref || undefined;
      }

      console.log('[NodePrimeImportDialog] Import configuration:', {
        importType,
        companyArtifactId: companyArtifact.id,
        companyArtifactParentRef: companyArtifact.parent_ref,
        parentId,
        selectedCount: selectedSelections.length,
        primeArtifactIds: selectedSelections.map(sel => ({
          id: sel.primeArtifact.id,
          name: sel.primeArtifact.name,
          artifact_type_id: sel.primeArtifact.artifact_type_id,
          artifact_type_name: sel.primeArtifact.artifact_type_name
        }))
      });

      // Build import nodes
      const importNodes = [
        {
          company_artifact_id: companyArtifact.id,
          parent_id: parentId, // Send as string or undefined (will be converted to null in JSON)
          prime_selections: selectedSelections.map((sel) => ({
            prime_artifact_id: sel.primeArtifact.id,
            company_name: sel.companyName,
            selected: true,
          })),
        },
      ];

      // Prepare the import request
      const importRequest: PrimeToCompanyImportRequest = {
        company_id: companyId,
        prime_artifact_ids: selectedSelections.map((sel) => sel.primeArtifact.id),
        import_nodes: importNodes,
      };

      console.log('[NodePrimeImportDialog] Sending import request:', {
        company_id: importRequest.company_id,
        import_nodes: importRequest.import_nodes.map(node => ({
          company_artifact_id: node.company_artifact_id,
          parent_id: node.parent_id,
          selections_count: node.prime_selections.length
        }))
      });

      // Call the import API
      const result = await PrimeToCompanyImportService.importPrimeToCompany(importRequest);

      console.log('[NodePrimeImportDialog] Import completed:', result);

      // Check for errors
      const importedCount = result.imported_count ?? result.created_artifacts ?? 0;
      const errors = result.errors ?? [];

      if (errors.length > 0) {
        const errorMessage = `Import completed with ${errors.length} error(s):\n${errors.join('\n')}`;
        setError(errorMessage);
        console.error('[NodePrimeImportDialog] Import errors:', errors);
        
        // If some artifacts were imported, still refresh
        if (importedCount > 0) {
          onImportComplete();
          onClose();
        }
        return;
      }

      if (importedCount === 0) {
        setError('No artifacts were imported. Please check your selections and try again.');
        return;
      }

      // Success - close dialog and refresh tree
      console.log(`[NodePrimeImportDialog] Successfully imported ${importedCount} artifacts`);
      onImportComplete();
      onClose();
    } catch (err: any) {
      console.error('[NodePrimeImportDialog] Import error:', err);
      
      if (err.response?.status === 404) {
        setError('Import endpoint not found. The server may need to be updated.');
      } else {
        setError(
          err.response?.data?.detail ||
          err.response?.data?.message ||
          (err instanceof Error ? err.message : 'Failed to import prime artifacts')
        );
      }
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setSelections([]);
    setPrimeArtifacts([]);
    setImportType('children');
    onClose();
  };

  const selectedCount = selections.filter((sel) => sel.selected).length;

  return (
    <Modal
      title={`Import from Prime: ${companyArtifact.name}`}
      open={isOpen}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="import"
          type="primary"
          onClick={handleImport}
          disabled={selectedCount === 0 || isImporting || isLoading}
          loading={isImporting}
        >
          {isImporting ? 'Importing...' : `Import ${selectedCount} Artifact${selectedCount !== 1 ? 's' : ''}`}
        </Button>,
      ]}
      width={800}
      destroyOnHidden
    >
      {!hasPrimeArtifact ? (
        <Alert
          message="No Prime Artifact Linked"
          description="This artifact does not have a linked prime artifact. Cannot import children or peers."
          type="warning"
          showIcon
        />
      ) : (
        <>
          <div style={{ marginBottom: '16px' }}>
            <Radio.Group
              value={importType}
              onChange={(e) => setImportType(e.target.value)}
              disabled={isLoading || isImporting}
            >
              <Radio value="children">Import Children</Radio>
              <Radio value="peers">Import Peers</Radio>
            </Radio.Group>
          </div>

          {error && (
            <Alert
              message={error}
              type={error.includes('error') ? 'error' : 'warning'}
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <p style={{ marginTop: '16px' }}>
                Loading {importType === 'children' ? 'children' : 'peers'}...
              </p>
            </div>
          ) : (
            <div className={styles.primeImportList}>
              {selections.length === 0 ? (
                <Alert
                  message="No Artifacts Available"
                  description={
                    importType === 'children'
                      ? 'This prime artifact has no children, or all children are already imported.'
                      : 'This prime artifact has no peers, or all peers are already imported.'
                  }
                  type="info"
                  showIcon
                />
              ) : (
                selections.map((selection) => (
                  <div key={selection.primeArtifact.id} className={styles.primeImportItem}>
                    <div className={styles.primeImportItemHeader}>
                      <Checkbox
                        checked={selection.selected}
                        onChange={(e) =>
                          handleSelectionChange(selection.primeArtifact.id, e.target.checked)
                        }
                      >
                        <strong>{selection.primeArtifact.name}</strong>
                      </Checkbox>
                    </div>
                    {selection.selected && (
                      <div className={styles.primeImportItemDetails}>
                        <div style={{ marginTop: '8px' }}>
                          <label>Company Artifact Name:</label>
                          <Input
                            value={selection.companyName}
                            onChange={(e) =>
                              handleNameChange(selection.primeArtifact.id, e.target.value)
                            }
                            placeholder={selection.primeArtifact.name}
                            style={{ marginTop: '4px' }}
                          />
                        </div>
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                          <div>
                            <strong>Type:</strong> {selection.primeArtifact.artifact_type_name || selection.primeArtifact.artifact_type_id || 'N/A'}
                          </div>
                          {selection.primeArtifact.description && (
                            <div style={{ marginTop: '4px' }}>
                              <strong>Description:</strong> {selection.primeArtifact.description}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

